import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Divider } from '@mui/material';
import type { ArbitrageOpportunity } from '../../types/opportunity';
import { getExchangeName, getQuoteCurrency } from '../../types/opportunity';
import { FeeStructure } from '../../models/FeeStructure';
import { useAppSelector } from '../../app/hooks';
import { selectNetworkStatus } from '../../features/networkStatus/networkStatusSlice';
import type { CryptoNetwork } from '../../types/networkStatus';
import { formatPrice, formatVolume, formatPercentage } from '../../utils/formatters';

interface ProfitCalculationTooltipProps {
  opportunity: ArbitrageOpportunity;
}

export const ProfitCalculationTooltip: React.FC<ProfitCalculationTooltipProps> = ({ opportunity }) => {
  const { t } = useTranslation();
  // Get exchange names and quote currencies
  const buyExchangeName = getExchangeName(opportunity.buy_exchange);
  const sellExchangeName = getExchangeName(opportunity.sell_exchange);
  const buyQuote = getQuoteCurrency(opportunity.buy_quote);
  const sellQuote = getQuoteCurrency(opportunity.sell_quote);
  
  // Use the pre-calculated fees from the opportunity (already computed correctly)
  const buyTakerFee = opportunity.buy_fee || 0;
  const sellMakerFee = opportunity.sell_fee || 0;
  
  // Get network status for withdrawal fees
  const networkStatus = useAppSelector(selectNetworkStatus);
  
  // Use the pre-calculated values from computeDisplayProperties
  const maxExecutableVolume = opportunity.max_volume || 0;
  const totalBuyCost = opportunity.total_buy_value || 0;
  
  // Find withdrawal fee from network status - use the cheapest matching network
  let withdrawalFeeAmount = 0;  // Fee amount in base currency
  let withdrawalNetwork = '';
  let depositConfirmations: number | undefined = undefined;
  let debugInfo = '';
  
  if (networkStatus && networkStatus.length > 0) {
    const buyExchangeStatus = networkStatus[opportunity.buy_exchange];
    const sellExchangeStatus = networkStatus[opportunity.sell_exchange];
    
    debugInfo = `Network data: Buy=${!!buyExchangeStatus}, Sell=${!!sellExchangeStatus}`;
    
    if (buyExchangeStatus && sellExchangeStatus) {
      // Find the base crypto in both exchanges
      const buyCrypto = buyExchangeStatus.cryptos.find(c => c.symbol === opportunity.base);
      const sellCrypto = sellExchangeStatus.cryptos.find(c => c.symbol === opportunity.base);
      
      if (!buyCrypto) {
        debugInfo = `${opportunity.base} not found in ${buyExchangeName} network status`;
      } else if (!sellCrypto) {
        debugInfo = `${opportunity.base} not found in ${sellExchangeName} network status`;
      } else {
        debugInfo += `, Crypto: Buy=✓, Sell=✓`;
      }
      
      if (buyCrypto && sellCrypto) {
        // Find matching networks between buy and sell exchanges
        const matchingNetworks: { network: string; fee: number; confirmations?: number }[] = [];
        
        // Get available networks for debugging
        const buyNetworksAll = buyCrypto.networks.map(n => 
          `${n.network}(withdraw:${n.withdraw_enabled}, fee:${n.withdraw_fee})`
        );
        const sellNetworksAll = sellCrypto.networks.map(n => 
          `${n.network}(deposit:${n.deposit_enabled})`
        );
        
        debugInfo += `\nBuy networks: ${buyNetworksAll.join(', ') || 'none'}`;
        debugInfo += `\nSell networks: ${sellNetworksAll.join(', ') || 'none'}`;
        
        for (const buyNet of buyCrypto.networks) {
          // Only consider networks where withdrawal is enabled on buy exchange
          if (!buyNet.withdraw_enabled || buyNet.withdraw_fee === undefined || buyNet.withdraw_fee === null) {
            continue;
          }
          
          // Find the same network on sell exchange where deposit is enabled
          const sellNet = sellCrypto.networks.find(sn => {
            if (!sn.deposit_enabled) return false;
            
            // Direct match
            if (sn.network === buyNet.network) return true;
            
            // Check for common network variations (case-insensitive)
            const buyNetNorm = buyNet.network.toUpperCase();
            const sellNetNorm = sn.network.toUpperCase();
            
            // ETH variations
            if ((buyNetNorm === 'ETH' || buyNetNorm === 'ERC20' || buyNetNorm === 'ETHEREUM') &&
                (sellNetNorm === 'ETH' || sellNetNorm === 'ERC20' || sellNetNorm === 'ETHEREUM')) {
              return true;
            }
            
            // BSC variations
            if ((buyNetNorm === 'BSC' || buyNetNorm === 'BEP20' || buyNetNorm === 'BNB') &&
                (sellNetNorm === 'BSC' || sellNetNorm === 'BEP20' || sellNetNorm === 'BNB')) {
              return true;
            }
            
            // TRON variations
            if ((buyNetNorm === 'TRX' || buyNetNorm === 'TRC20' || buyNetNorm === 'TRON') &&
                (sellNetNorm === 'TRX' || sellNetNorm === 'TRC20' || sellNetNorm === 'TRON')) {
              return true;
            }
            
            return false;
          });
          
          if (sellNet) {
            matchingNetworks.push({
              network: buyNet.name || buyNet.network,
              fee: buyNet.withdraw_fee,
              confirmations: sellNet.confirmations  // Deposit confirmations on sell exchange
            });
          }
        }
        
        // Use the cheapest matching network
        if (matchingNetworks.length > 0) {
          const cheapest = matchingNetworks.reduce((min, net) => 
            net.fee < min.fee ? net : min
          );
          withdrawalFeeAmount = cheapest.fee;
          withdrawalNetwork = cheapest.network;
          depositConfirmations = cheapest.confirmations;
          debugInfo += `\nMatched: ${matchingNetworks.length} networks`;
        } else {
          debugInfo += '\nNo matching networks found';
        }
      }
    } else {
      debugInfo += ' - No exchange data available';
    }
  }
  
  // Calculate volume after withdrawal fee
  // When trade amount is limited, we should only deduct withdrawal fee from what we actually have
  const maxExecutableVolumeAfterWithdrawal = Math.max(0, maxExecutableVolume - withdrawalFeeAmount);
  
  // Calculate sell revenue with reduced volume after withdrawal
  let remainingVolume = maxExecutableVolumeAfterWithdrawal;
  let grossSellRevenue = 0;
  
  for (const level of opportunity.sell_levels) {
    const volumeAtLevel = Math.min(remainingVolume, level.volume);
    if (volumeAtLevel <= 0) break;
    
    grossSellRevenue += level.price * volumeAtLevel;
    remainingVolume -= volumeAtLevel;
  }
  
  // Apply sell maker fee to get net revenue
  const netSellRevenue = grossSellRevenue * (1 - sellMakerFee);
  
  // Convert buy cost to sell quote currency for comparison
  const totalBuyCostInSellQuote = opportunity.quote_conversion_rate !== 1.0 
    ? totalBuyCost * opportunity.quote_conversion_rate 
    : totalBuyCost;
  
  // Calculate fees
  const buyFeeAmount = totalBuyCost * buyTakerFee;
  const sellFeeAmount = grossSellRevenue * sellMakerFee;
  const totalFees = buyFeeAmount + sellFeeAmount;
  
  // Calculate profit (including withdrawal fee impact)
  const netProfit = netSellRevenue - totalBuyCostInSellQuote;
  const profitPercentage = totalBuyCostInSellQuote > 0
    ? ((netSellRevenue / totalBuyCostInSellQuote) - 1) * 100
    : 0;
  
  return (
    <Box sx={{ p: 1.5, minWidth: 300, maxWidth: 450 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {t('profitTooltip.title')}
      </Typography>
      
      <Divider sx={{ my: 1 }} />
      
      {/* Net Profit Summary First */}
      <Box sx={{ mb: 1, bgcolor: 'rgba(0, 255, 255, 0.05)', p: 1, borderRadius: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          {t('profitTooltip.netProfit')}
        </Typography>
        <Box sx={{ ml: 1, mt: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
            <Typography variant="caption">{t('profitTooltip.profit')} ({sellQuote}):</Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: netProfit > 0 ? 'success.main' : 'error.main',
                fontWeight: 'bold'
              }}
            >
              {formatPrice(netProfit, sellQuote[0])}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
            <Typography variant="caption">{t('profitTooltip.profitPercent')}:</Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: profitPercentage > 0 ? 'success.main' : 'error.main',
                fontWeight: 'bold'
              }}
            >
              {formatPercentage(profitPercentage, 2)}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      {/* Buy Side */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {t('profitTooltip.buySide', { exchange: buyExchangeName })}
        </Typography>
        <Box sx={{ ml: 1, mt: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.price')}:</Typography>
            <Typography variant="caption">{formatPrice(opportunity.buy_price || 0, buyQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.volume')}:</Typography>
            <Typography variant="caption">{formatVolume(maxExecutableVolume / (1 - buyTakerFee), 8)} {opportunity.base}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.cost')}:</Typography>
            <Typography variant="caption">{formatPrice(totalBuyCost, buyQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.takerFee', { percent: (buyTakerFee * 100).toFixed(2) })}:</Typography>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              -{formatVolume((maxExecutableVolume / (1 - buyTakerFee)) * buyTakerFee, 8)} {opportunity.base}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{withdrawalNetwork ? t('profitTooltip.withdrawFeeNetwork', { network: withdrawalNetwork }) : t('profitTooltip.withdrawFee')}:</Typography>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              -{formatVolume(withdrawalFeeAmount, 8)} {opportunity.base}
            </Typography>
          </Box>
          {depositConfirmations !== undefined && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption">Deposit Confirmations:</Typography>
              <Typography variant="caption" sx={{ color: 'info.main' }}>
                {depositConfirmations} confirmations
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
            <Typography variant="caption">{t('profitTooltip.volumeAfterFee')}:</Typography>
            <Typography variant="caption">{formatVolume(maxExecutableVolume, 8)} {opportunity.base}</Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      {/* Sell Side */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
          {t('profitTooltip.sellSide', { exchange: sellExchangeName })}
        </Typography>
        <Box sx={{ ml: 1, mt: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.price')}:</Typography>
            <Typography variant="caption">{formatPrice(opportunity.sell_price || 0, sellQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.volumeAfterWithdraw')}:</Typography>
            <Typography variant="caption">{formatVolume(maxExecutableVolumeAfterWithdrawal, 8)} {opportunity.base}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.grossRevenue')}:</Typography>
            <Typography variant="caption">{formatPrice(grossSellRevenue, sellQuote[0])}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t('profitTooltip.makerFee', { percent: (sellMakerFee * 100).toFixed(2) })}:</Typography>
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              -{formatVolume(maxExecutableVolumeAfterWithdrawal * sellMakerFee, 8)} {opportunity.base}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
            <Typography variant="caption">{t('profitTooltip.netRevenue')}:</Typography>
            <Typography variant="caption">{formatPrice(netSellRevenue, sellQuote[0])}</Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      {/* Conversion Rate if needed */}
      {opportunity.quote_conversion_rate !== 1.0 && (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {t('profitTooltip.currencyConversion')}
            </Typography>
            <Box sx={{ ml: 1, mt: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">{t('profitTooltip.rate', { from: buyQuote, to: sellQuote })}:</Typography>
                <Typography variant="caption">{opportunity.quote_conversion_rate.toFixed(6)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">{t('profitTooltip.buyCostIn', { currency: sellQuote })}:</Typography>
                <Typography variant="caption">{formatPrice(totalBuyCostInSellQuote, sellQuote[0])}</Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 1 }} />
        </>
      )}
      
      
      
      
      {/* Additional Info */}
      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: '0.65rem',
          color: 'text.secondary',
          display: 'block',
          mt: 0.5
        }}
      >
        {t('profitTooltip.additionalInfo.buyTaker')}
      </Typography>
      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: '0.65rem',
          color: 'text.secondary',
          display: 'block'
        }}
      >
        {t('profitTooltip.additionalInfo.sellMaker')}
      </Typography>
      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: '0.65rem',
          color: 'text.secondary',
          display: 'block'
        }}
      >
        {t('profitTooltip.additionalInfo.networkFees')}
      </Typography>
    </Box>
  );
};